import { useRouter } from 'next/router';
import { useState, useEffect, useRef } from 'react';
import { isUSState, getMostPopulousCity } from '../utils/stateCapitals';

// Common cities people search for - to provide suggestions
const COMMON_CITIES = [
  'Seattle, WA',
  'Bellevue, WA',
  'Bothell, WA',
  'Tacoma, WA',
  'Kirkland, WA',
  'Redmond, WA',
  'Everett, WA',
  'Federal Way, WA',
  'Lynnwood, WA',
  'Kent, WA',
  'Portland, OR',
  'Eugene, OR',
  'Salem, OR',
  'Bend, OR',
  'San Francisco, CA',
  'Los Angeles, CA',
  'San Diego, CA',
  'Sacramento, CA',
  'Fresno, CA',
  'San Jose, CA',
  'Oakland, CA',
  'Bakersfield, CA',
  'Anaheim, CA',
  'Santa Ana, CA',
  'Riverside, CA',
  'Stockton, CA',
  'Irvine, CA',
  'New York, NY',
  'Chicago, IL',
  'Houston, TX',
  'Phoenix, AZ',
  'Miami, FL',
  'Dallas, TX',
  'Boston, MA',
  'Atlanta, GA',
  'Denver, CO',
  'Las Vegas, NV',
  'Washington, DC',
  'Philadelphia, PA',
  'Vancouver, WA',
  'Nashville, TN',
  'Detroit, MI',
  'Minneapolis, MN',
  'St. Louis, MO',
  'Tampa, FL',
  'Orlando, FL',
  'Austin, TX',
  'Charlotte, NC',
  'Indianapolis, IN',
  'Columbus, OH',
  'Cleveland, OH',
  'Pittsburgh, PA'
];

// Common ZIP codes (major cities)
const COMMON_ZIP_CODES = [
  '10001', // New York
  '90210', // Beverly Hills
  '98101', // Seattle
  '98004', // Bellevue
  '98012', // Bothell
  '98011', // Bothell
  '98021', // Bothell
  '98033', // Kirkland
  '98052', // Redmond
  '98201', // Everett
  '98003', // Federal Way
  '98036', // Lynnwood
  '98032', // Kent
  '97201', // Portland
  '94102', // San Francisco
  '90001', // Los Angeles
  '60601', // Chicago
  '77001', // Houston
  '85001', // Phoenix
  '33101', // Miami
  '75201', // Dallas
  '02108', // Boston
  '30301', // Atlanta
  '80201', // Denver
  '89101'  // Las Vegas
];

// All US states as suggestions
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 
  'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 
  'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 
  'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 
  'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

// Combine all suggestions into one array
const ALL_SUGGESTIONS = [...COMMON_CITIES, ...US_STATES, ...COMMON_ZIP_CODES];

export default function SearchBar({ className = '', defaultLocation = '', onLocationDetect }) {
  const router = useRouter();
  const [inputValue, setInputValue] = useState(defaultLocation || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  
  // Initialize with location from URL if available
  useEffect(() => {
    if (router.query.location) {
      setInputValue(router.query.location);
    } else if (defaultLocation) {
      setInputValue(defaultLocation);
    }
    
    // Clear the input if we're using current location
    if (router.query.usingCurrentLocation) {
      setInputValue('');
    }
  }, [router.query.location, defaultLocation, router.query.usingCurrentLocation]);

  // Categorize and filter suggestions based on input
  const filterSuggestions = (input) => {
    if (!input || input.length < 2) return [];
    
    const lowercaseInput = input.toLowerCase();
    
    // Create separate arrays for cities, states, and zip codes
    const matchingCities = [];
    const matchingStates = [];
    const matchingZipCodes = [];
    
    // Filter and categorize suggestions
    ALL_SUGGESTIONS.forEach(suggestion => {
      const lowercaseSuggestion = suggestion.toLowerCase();
      
      // Check if suggestion includes the input text
      if (lowercaseSuggestion.includes(lowercaseInput)) {
        // Categorize based on the type of suggestion
        if (/^\d{5}$/.test(suggestion)) {
          matchingZipCodes.push(suggestion);
        } else if (US_STATES.includes(suggestion)) {
          matchingStates.push(suggestion);
        } else {
          matchingCities.push(suggestion);
        }
      }
    });
    
    // Special sorting for cities to prioritize those that start with the input
    const sortedCities = matchingCities.sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      
      // If one starts with the input and the other doesn't, prioritize the one that starts with it
      const aStartsWith = aLower.startsWith(lowercaseInput);
      const bStartsWith = bLower.startsWith(lowercaseInput);
      
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      
      // Otherwise, sort alphabetically
      return aLower.localeCompare(bLower);
    });
    
    // Combine all matches with cities first, then states, then zip codes
    const sortedSuggestions = [
      ...sortedCities, 
      ...matchingStates.sort(), 
      ...matchingZipCodes.sort()
    ].slice(0, 10); // Limit to 10 suggestions
    
    return sortedSuggestions;
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Reset active suggestion index
    setActiveSuggestionIndex(-1);
    
    // Update suggestions
    const filteredSuggestions = filterSuggestions(value);
    setSuggestions(filteredSuggestions);
    setShowSuggestions(filteredSuggestions.length > 0);
  };
  
  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // If no suggestions are showing, do nothing
    if (!showSuggestions || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestionIndex(prevIndex => 
          prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestionIndex(prevIndex => 
          prevIndex > 0 ? prevIndex - 1 : -1
        );
        break;
      case 'Enter':
        if (activeSuggestionIndex >= 0) {
          e.preventDefault();
          handleSuggestionClick(suggestions[activeSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    
    // Direct access to input value through the form
    const searchLocation = inputValue.trim();
    
    // Use default location if empty
    if (!searchLocation || searchLocation === '') {
      // Redirect to search page with default sorting by rating
      router.push('/search?sort=rating&search_initiated=true');
      return;
    }
    
    // Check if the search is for a known commonly misspelled city
    const misspelledCities = {
      'bothel': 'Bothell, WA',
      'belvu': 'Bellevue, WA',
      'bellview': 'Bellevue, WA',
      'seatle': 'Seattle, WA',
      'seatel': 'Seattle, WA',
      'tacoma': 'Tacoma, WA',
      'vancuver': 'Vancouver, WA',
      'portland': 'Portland, OR',
      'fresno': 'Fresno, CA',
      'pheonix': 'Phoenix, AZ',
      'phenix': 'Phoenix, AZ',
      'lv': 'Las Vegas, NV',
      'vegas': 'Las Vegas, NV'
    };
    
    const lowercaseSearch = searchLocation.toLowerCase();
    
    // Check if this is a known misspelling
    if (misspelledCities[lowercaseSearch]) {
      router.push(`/search?location=${encodeURIComponent(misspelledCities[lowercaseSearch])}&search_initiated=true`);
      return;
    }
    
    // Check if this is a state name or abbreviation
    const stateData = isUSState(searchLocation);
    if (stateData) {
      const { city, state, lat, lng } = stateData;
      console.log(`Search for state "${searchLocation}" redirecting to ${city}, ${state}`);
      
      router.push({
        pathname: '/search',
        query: { 
          location: `${city}, ${state}`,
          lat,
          lng,
          search_initiated: true
        }
      });
      return;
    }
    
    // Redirect to search page with location
    router.push(`/search?location=${encodeURIComponent(searchLocation)}&search_initiated=true`);
  };

  // Close suggestions when clicking outside
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target) && 
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <form onSubmit={handleSubmit} className={`flex flex-col sm:flex-row max-w-lg mx-auto ${className}`}>
      <div className="flex-grow relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
        </div>
        
        <input
          ref={inputRef}
          name="searchInput"
          type="text"
          placeholder="Enter ZIP code, city, or state"
          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg sm:rounded-r-none shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.length >= 2) {
              const filteredSuggestions = filterSuggestions(inputValue);
              setSuggestions(filteredSuggestions);
              setShowSuggestions(filteredSuggestions.length > 0);
            }
          }}
          // Disable browser autocomplete
          autoComplete="off" 
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          aria-label="City, state, or ZIP code"
        />
        
        {/* Custom autocomplete suggestions */}
        {showSuggestions && (
          <ul 
            ref={suggestionsRef}
            className="absolute z-10 bg-white w-full mt-1 rounded-md shadow-lg border border-gray-200 max-h-60 overflow-auto"
          >
            {suggestions.map((suggestion, index) => (
              <li 
                key={index} 
                className={`px-4 py-2 cursor-pointer text-gray-700 ${
                  index === activeSuggestionIndex ? 'bg-blue-50 font-semibold' : 'hover:bg-blue-50'
                }`}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setActiveSuggestionIndex(index)}
              >
                {suggestion}
              </li>
            ))}
          </ul>
        )}
      </div>
      
      <button
        type="submit"
        disabled={isLoading}
        className="mt-2 sm:mt-0 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg sm:rounded-l-none transition"
      >
        {isLoading ? 'Searching...' : 'Find Help Now'}
      </button>
    </form>
  );
}